import type { ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import remarkGfm from 'remark-gfm'
import type { DocumentHeading } from '../../lib/document-reader'

interface DocumentMarkdownProps {
  content: string
  headings: DocumentHeading[]
}

export function DocumentMarkdown({ content, headings }: DocumentMarkdownProps) {
  let headingIndex = 0
  const renderHeading = (Heading: 'h2' | 'h3') =>
    function MarkdownHeading({ children }: { children?: ReactNode }) {
      const id = headings[headingIndex]?.id
      headingIndex += 1
      return <Heading id={id}>{children}</Heading>
    }

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeHighlight]}
      components={{
        h1: renderHeading('h2'),
        h2: renderHeading('h2'),
        h3: renderHeading('h3'),
        img: ({ src, alt, ...props }) => (
          <img
            src={src ?? ''}
            alt={alt || ''}
            loading="lazy"
            onError={(event) => {
              event.currentTarget.style.display = 'none'
            }}
            {...props}
          />
        ),
        a: ({ href, children, ...props }) => {
          const isExternal = href?.startsWith('http')
          return (
            <a
              href={href}
              {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              {...props}
            >
              {children}
            </a>
          )
        },
      }}
    >
      {content}
    </ReactMarkdown>
  )
}
